import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTimeoutFn } from 'react-use';
import styled from 'styled-components';
import { useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Checkbox } from '../../../../ui/Checkbox';
import { LoadingBar } from '../../../../ui/LoadingBar';
import { SliderWithInput } from '../../../../ui/SliderWithInput';
import { useResolve } from '../../../Container';
import { EditorSession } from '../../../canvas/EditorSession';
import { MyProfileInfo } from '../../../canvas/MyProfileInfo';
import { ParticipantManager } from '../../../canvas/participants/ParticipantManager';
import { IsElevated } from '../../../canvas/workflow/IsElevated';
import { ButtonStrip, Copy, Flexer, Spacer, Title } from '../../../dashboard/elements/LayoutElements';
import { pollFinish, pollRespond, pollSave } from '../PollCommands';
import { LivePoll, LivePollState, PollResults } from '../PollTypes';
import { Button, Colors } from './../../../../ui';

const ButtonList = styled.div`
    & > * {
        margin-right: .5rem;
    }
`;

type RespondCallback = (response: number[]) => void;

const SingleOptionInput = React.memo<{ options: string[], onRespond: RespondCallback }>(({ options, onRespond }) =>
    <ButtonList>
        {options.map((x, i) =>
            <Button key={`${x}${i}`} label={x} onAction={() => onRespond([i])} />
        )}
    </ButtonList>
);

const MultiOptionInput = React.memo<{ options: string[], onRespond: RespondCallback }>(({ options, onRespond }) => {

    const [picks, setPicks] = useState([] as number[]);
    const onToggle = (i: number) => {
        setPicks(x => x.includes(i) ? x.filter(e => e !== i) : x.concat(i));
    };
    const onRespondWithPicks = useCallback(() => onRespond(picks), [onRespond, picks]);

    return <Flexer>
        {options.map((x, i) =>
            <Flexer key={`${x}${i}`} margin="0 0 .5 0">
                <Checkbox label={x} checked={picks.includes(i)} onChange={() => onToggle(i)} />
            </Flexer>
        )}
        <Spacer f={1} />
        <ButtonStrip align="right">
            <Button label="Submit" onAction={onRespondWithPicks} />
        </ButtonStrip>
    </Flexer>;
});

const ScaleOptionInput = React.memo<{ options: string[], onRespond: RespondCallback }>(({ options, onRespond }) => {
    const min = parseInt(options[0]) || 0;
    const max = parseInt(options[1]) || 10;

    const [value, setValue] = useState(0);
    const onRespondWithValue = useCallback(() => onRespond([value]), [onRespond, value]);

    return <Flexer relative>
        <SliderWithInput {...{ value, min, max }} onChange={setValue} />
        <Spacer f={1} />
        <ButtonStrip align="right">
            <Button label="Submit" onAction={onRespondWithValue} />
        </ButtonStrip>
    </Flexer>;
});

const AutoInput = React.memo<{ after: number, onRespond: RespondCallback }>(({ after, onRespond }) => {
    useTimeoutFn(() => void (onRespond([])), after);
    return null;
});

const InputView = React.memo<{ data: LivePollState }>(({ data }) => {

    const dispatch = useResolve(CommandDispatcher);
    const { type, options } = data;

    const onRespond = (r: number[]) => dispatch(pollRespond, r);

    return <>
        {type === 'single' && <SingleOptionInput {...{ options, onRespond }} />}
        {type === 'multi' && <MultiOptionInput {...{ options, onRespond }} />}
        {type === 'scale' && <ScaleOptionInput {...{ options, onRespond }} />}
        <AutoInput {...{ after: 30000, onRespond }} />
    </>;
});

const WaitingView = React.memo<{ count: number }>(({ count }) =>
    <Flexer>
        <Copy>
            Waiting for {count} more people to vote...
        </Copy>
    </Flexer>
);

const ResultsView = React.memo<{ data: PollResults }>(({ data }) =>
    <Flexer>
        {data.items.map((x, i) =>
            <Flexer key={`${x.option}/${i}`} margin="0 0 1 0">
                <Flexer flex="row" justify="space-between">
                    <Copy bold>{x.option}</Copy>
                    <Copy bolder>
                        {x.format === '%' ? <>
                            {Math.round((x.numer / x.denom) * 100) || 0}%</> : <>
                            {x.numer || 0} / {x.denom || 0}
                        </>}
                    </Copy>
                </Flexer>
                <LoadingBar color={x.color} progress={x.numer / x.denom} />
            </Flexer>
        )}
    </Flexer>
);

const ActionsView = () => {
    const dispatch = useResolve(CommandDispatcher);
    return <>
        <Spacer />
        <ButtonStrip align="right">
            <Button data-action="editor.activePoll.save" onAction={() => dispatch(pollSave)}>Add To Board</Button>
            <Button data-action="editor.activePoll.finish" onAction={() => dispatch(pollFinish)}>Finish</Button>
        </ButtonStrip>
    </>;
};

export const createPollView = (poll: LivePoll) => () => <PollView poll={poll} />;

export const PollView = React.memo<{ poll: LivePoll }>(({ poll }) => {

    const me = useResolve(EditorSession).get(MyProfileInfo);
    const elevated = useResolve(EditorSession).get(IsElevated);
    const ptpts = useResolve(ParticipantManager);

    const state = useObserver(poll);
    const results = useMemo(() => PollResults.from(state), [state]);

    const [waitingFor, setWaitingFor] = useState(1);
    const waitingForMe = !state[me.id];

    useEffect(() => {
        if (!waitingFor) return;
        const here = ptpts.filter(x => x.presence === 'active');
        setWaitingFor(Math.max(0, here.length - results.voters.length));
    }, [waitingFor, results]);


    return <>
        <LoadingBar color={Colors.primary} etc={!!waitingFor ? 30000 : 500} />
        <Flexer padding="1.5">
            {/* <Copy>{me.name} asks...</Copy> */}
            <Title>{state.question}</Title>
            <Spacer />
            {waitingForMe && <InputView data={state} />}
            {!waitingForMe && waitingFor > 0 && <WaitingView count={waitingFor} />}
            {!waitingFor && <ResultsView data={results} />}
            {!waitingFor && elevated && <ActionsView />}
        </Flexer>
    </>;

});
